import React, { useEffect, useState } from 'react'
import PqrSectionModal from './PqrSectionModal'

export interface IdentifiersValue {
  pqrNumber: string
  revision: string
}

interface Props {
  open: boolean
  initial: IdentifiersValue
  editInWpsHref?: string
  onApply: (next: IdentifiersValue) => void
  onCancel: () => void
}

/**
 * Identifiers modal body — the canonical example for all PQR Preview section
 * modals. Owns a local working copy keyed off `initial`. Apply commits the
 * working copy via `onApply`; Cancel discards (with the shell's standard
 * dirty-confirm guard).
 *
 * The modal does NOT validate "required" here — Slice 01 intentionally lets
 * users park drafts with blank identifiers. The toolbar's Save & Generate
 * button is what gates required fields.
 */
export default function IdentifiersModal({
  open,
  initial,
  editInWpsHref,
  onApply,
  onCancel
}: Props) {
  const [draft, setDraft] = useState<IdentifiersValue>(initial)

  // Reset the working copy whenever the modal opens with a fresh snapshot,
  // so reopening after Cancel always starts from the current staged value.
  useEffect(() => {
    if (open) setDraft(initial)
  }, [open, initial.pqrNumber, initial.revision]) // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty =
    draft.pqrNumber !== initial.pqrNumber || draft.revision !== initial.revision

  return (
    <PqrSectionModal
      open={open}
      title="Edit identifiers"
      // editInWpsHref={editInWpsHref}
      isModalDirty={isDirty}
      onApply={() => onApply(draft)}
      onCancel={onCancel}
      panelMaxWidthClassName="max-w-2xl"
    >
      <div className="grid grid-cols-2 gap-4">
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">PQR No.</span>
          <input
            type="text"
            value={draft.pqrNumber}
            onChange={(e) => setDraft((d) => ({ ...d, pqrNumber: e.target.value }))}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"
            autoFocus
            data-testid="pqr-number-input"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">Rev.</span>
          <input
            type="text"
            value={draft.revision}
            onChange={(e) => setDraft((d) => ({ ...d, revision: e.target.value }))}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"
            data-testid="pqr-revision-input"
          />
        </label>
       
      </div>
      <p className="text-xs text-gray-500 mt-4">
          Changes are staged in the preview. Click <strong>Save</strong> or{' '}
          <strong>Save &amp; Generate Report</strong> on the page header to persist them.
        </p>
    </PqrSectionModal>
  )
}
